"use client";

import type React from "react";
import { useEffect, useState } from "react";
import { API_ORIGIN, homePageAPI, categoryAPI, type Collection } from "../services/api";
import { VideoPlayer } from "../components/ui/VideoPlayer";
import { productThumbUrl } from "../utils/mediaUrls";
import "./styles.css";
import { Autoplay, Navigation } from "swiper/modules";

import { Swiper, SwiperSlide } from 'swiper/react';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import { SeoHelmet } from "../components/SeoHelmet";
import LogoLoader from "../components/ui/LogoLoader";
import homeCollage from "../assets/mmain-mobile.jpg";
import homeHeroPhoto from "../assets/mmain.jpg";
import { DEFAULT_DESCRIPTION, HOME_SEO_TITLE, HOME_VISIBLE_LEAD } from "../config/seo";



interface Advantage {
  title: string;
  description: string;
  icon: string;
}

interface HomePageData {
  heroTitle: string;
  heroSubtitle: string;
  heroVideo: string;
  advantages: Advantage[];
  /** С бэка может прийти массив id или полных объектов коллекций (TypeORM). */
  popularCollections: (number | Collection)[];
}

function splitHeroTitle(title: string) {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return title;
  const marker = words.indexOf("ПРЕОБРАЖАЮТ");
  const cut = marker >= 0 ? marker + 1 : Math.ceil(words.length / 2);
  return (
    <>
      {words.slice(0, cut).join(" ")}
      <br />
      {words.slice(cut).join(" ")}
    </>
  );
}

const HomePage: React.FC = () => {
  const [data, setData] = useState<HomePageData | null>(null);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pageData = await homePageAPI.getPage();
        setData(pageData);

        const response = await categoryAPI.getAll();
        const rawPopular = pageData.popularCollections ?? [];
        const popularIds = rawPopular.map((item) =>
          typeof item === "number" ? item : item.id,
        );
        const popular = response.data.filter((col: Collection) =>
          popularIds.includes(col.id),
        );
        setCollections(popular);
      } catch (error) {
        console.error("Ошибка загрузки данных:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <>
        <SeoHelmet title={HOME_SEO_TITLE} description={DEFAULT_DESCRIPTION} path="/" />
        <LogoLoader />
      </>
    );
  }
  if (!data) {
    return (
      <>
        <SeoHelmet title={HOME_SEO_TITLE} description={DEFAULT_DESCRIPTION} path="/" />
        <div className="home-seo-fallback">
          <p className="home-static-lead">{HOME_VISIBLE_LEAD}</p>
          <p>Не удалось загрузить блок с акциями и коллекциями. Попробуйте обновить страницу или откройте <a href="/catalog">каталог тканей</a>.</p>
        </div>
      </>
    );
  }

  const homeDesc =
    data.heroSubtitle?.trim() ||
    `${data.heroTitle}. ${DEFAULT_DESCRIPTION}`;

  return (
    <div className="hero-page-container">
      <SeoHelmet title={HOME_SEO_TITLE} description={homeDesc.slice(0, 320)} path="/" />
      <div
        className="home-page"
      >
        <section className="home-hero-band">
        <img
          className="home-hero-photo"
          src={homeHeroPhoto}
          alt=""
        />
        <img
          className="home-collage"
          src={homeCollage}
          alt="Коллаж тканей и материалов"
        />
        {/* Hero секция */}
        <section className="hero">
          {/* Видео, если есть */}
          {/* {data.heroVideo && (
          <video
            autoPlay
            muted
            loop
            className="hero-video"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              zIndex: 1
            }}
          >
            <source
              src={(process.env.REACT_APP_API_URL + data.heroVideo).replaceAll(
                "\\",
                "/"
              )}
              type="video/mp4"
            />
            Ваш браузер не поддерживает видео.
          </video>
        )} */}

          {/* Оверлей с контентом */}
          <div
            className="hero-overlay"
          >
            <div className="hero-content reveal">
              <span className="hero-title">{splitHeroTitle(data.heroTitle)}</span>
              {/* <p className="hero-subtitle">{data.heroSubtitle}</p> */}
              <span className="hero-btn">
                <a href="/catalog" className="btn-luxury">
                  <span>Смотреть каталог</span>
                  <svg className="btn-luxury__arrow" viewBox="2.5 4.5 18 15" aria-hidden="true">
                    <path d="M4 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </a>
              </span>
            </div>
          </div>
        </section>
        </section>
        <section id="catalog" className="categories reveal reveal-delay-1">
          {/* <h2 className="section-title">Популярные коллекции</h2> */}
          {collections.length === 0 ? (
            null
          ) : (
            <Swiper
              modules={[Navigation, Autoplay]}
              spaceBetween={20}
              slidesPerView={2}
              navigation={true}
              loop
              speed={700}
              autoplay={{
                delay: 3500,
                disableOnInteraction: false,
                pauseOnMouseEnter: false,
              }}
              breakpoints={{
                320: {
                  slidesPerView: 1,
                  spaceBetween: 20,
                  autoplay: {
                    delay: 3500,
                    disableOnInteraction: false,
                  },
                },
                640: {
                  slidesPerView: 2,
                  spaceBetween: 30,
                  autoplay: {
                    delay: 3500,
                    disableOnInteraction: false,
                  },
                },
                768: {
                  slidesPerView: 2,
                  spaceBetween: 30,
                  autoplay: false,
                },
                1280: {
                  slidesPerView: 3,
                  spaceBetween: 40,
                  autoplay: false,
                },
                2000: {
                  slidesPerView: 4,
                  spaceBetween: 50,
                  autoplay: false,
                }
              }}
            >
              {collections.map((category) => {
                const featured =
                  category.products.find(
                    (product) => product.id === category.featuredProductId,
                  ) ?? category.products[0];
                const rel = featured?.videos?.[0];
                const norm = rel != null && String(rel).trim() ? String(rel).trim().replace(/\\/g, "/") : "";
                const videoSrc = norm ? `${API_ORIGIN}${norm.startsWith("/") ? "" : "/"}${norm}` : null;
                const imageRel = featured?.images?.[0];
                const imageNorm = imageRel != null && String(imageRel).trim() ? String(imageRel).trim().replace(/\\/g, "/") : "";
                const imageSrc = imageNorm
                  ? productThumbUrl(`${API_ORIGIN}${imageNorm.startsWith("/") ? "" : "/"}${imageNorm}`)
                  : null;

                return (
                <SwiperSlide key={category.id}>
                  <div className="category-card">
                    <div className="category-overlay">
                      {videoSrc ? (
                        <VideoPlayer className="category-video" src={videoSrc} />
                      ) : imageSrc ? (
                        <img className="category-video" src={imageSrc} alt={category.name} />
                      ) : null}
                      <div className="category-content">
                        <h3>{category.name}</h3>
                        <a
                          href={`/category/${category.id}`}
                          className="btn btn-outline"
                        >
                          Смотреть
                        </a>
                      </div>
                    </div>
                  </div>
                </SwiperSlide>
                );
              })}
            </Swiper>

          )}
        </section>
      </div>
    </div>

  );
};

export default HomePage;
